/**
 * An OpenAI-compatible provider — DeepSeek, OpenAI, OpenRouter and the like — behind the
 * same loop the Claude provider drives. The history stays in the Messages-API shape the
 * runtime keeps; this translates it to Chat Completions on the way out and turns the
 * streamed chunks back into the events and the final message the runtime expects.
 */
import type { AgentProvider, Message, MessageParam, ProviderRequest, ProviderRun, StreamEvent, TextBlockParam, ToolParam } from './providers';
import { t } from '../i18n/I18n';

export interface OpenAICompatibleOptions {
  /** The API root, e.g. `https://api.deepseek.com/v1` or `<origin>/api/agent/openai/v1`. */
  baseURL: string;
  /** Sent as a Bearer token; omitted when the proxy holds the key. */
  apiKey?: string;
  fetch?: typeof fetch;
  /** The provider's output ceiling; DeepSeek accepts 32000. */
  maxTokens?: number;
  /** DeepSeek models think before answering unless told not to; off is faster for tool-driven work. */
  thinking?: boolean;
}

export const DEEPSEEK_BASE_URL = 'https://api.deepseek.com/v1';
export const DEEPSEEK_MODELS: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'deepseek-v4-pro', label: 'DeepSeek V4 Pro' },
  { id: 'deepseek-flash', label: 'DeepSeek Flash' },
];

type ChatMessage =
  | { role: 'system' | 'user'; content: string }
  | { role: 'assistant'; content: string | null; tool_calls?: Array<{ id: string; type: 'function'; function: { name: string; arguments: string } }> }
  | { role: 'tool'; tool_call_id: string; content: string };

const IMAGE_NOTE = '[An image was attached here, but this model cannot see images. Ask for a text description or use get_scene.]';

/** Text out of any content block list; images become a note the model can act on. */
function flatten(content: unknown): string {
  if (typeof content === 'string') return content;
  if (!Array.isArray(content)) return '';
  return content.map(block => {
    const item = block as { type?: string; text?: string };
    if (item.type === 'text') return item.text ?? '';
    if (item.type === 'image') return IMAGE_NOTE;
    return '';
  }).filter(Boolean).join('\n');
}

/** Messages-API history → Chat Completions messages. Tool results become `tool` messages right after their call. */
export function toChatMessages(system: TextBlockParam[], history: MessageParam[]): ChatMessage[] {
  const out: ChatMessage[] = [];
  const systemText = system.map(block => block.text).join('\n\n').trim();
  if (systemText) out.push({ role: 'system', content: systemText });
  for (const message of history) {
    if (message.role === 'user') {
      if (typeof message.content === 'string') { out.push({ role: 'user', content: message.content }); continue; }
      const text: string[] = [];
      for (const block of message.content) {
        if (block.type === 'tool_result') out.push({ role: 'tool', tool_call_id: block.tool_use_id, content: flatten(block.content) || 'OK' });
        else if (block.type === 'text') text.push(block.text);
        else if (block.type === 'image') text.push(IMAGE_NOTE);
      }
      if (text.length) out.push({ role: 'user', content: text.join('\n') });
    } else if (message.role === 'assistant') {
      if (typeof message.content === 'string') { out.push({ role: 'assistant', content: message.content }); continue; }
      const text: string[] = [];
      const calls: NonNullable<Extract<ChatMessage, { role: 'assistant' }>['tool_calls']> = [];
      for (const block of message.content) {
        if (block.type === 'text') text.push(block.text);
        else if (block.type === 'tool_use') calls.push({ id: block.id, type: 'function', function: { name: block.name, arguments: JSON.stringify(block.input ?? {}) } });
      }
      out.push({ role: 'assistant', content: text.join('\n') || (calls.length ? null : ''), ...(calls.length ? { tool_calls: calls } : {}) });
    }
  }
  return out;
}

export function toChatTools(tools: ToolParam[]): Array<{ type: 'function'; function: { name: string; description?: string; parameters: unknown } }> {
  return tools.map(tool => ({ type: 'function', function: { name: tool.name, description: tool.description, parameters: tool.input_schema } }));
}

export class ProviderHttpError extends Error {
  constructor(readonly status: number, message: string) { super(message); this.name = 'ProviderHttpError'; }
}

/** A readable reason from an OpenAI-compatible error answer. */
export function describeOpenAIError(status: number, body: string): string {
  let detail = '';
  try { const parsed = JSON.parse(body) as { error?: { message?: string } | string; message?: string }; detail = (typeof parsed.error === 'string' ? parsed.error : parsed.error?.message) ?? parsed.message ?? ''; } catch { detail = body.slice(0, 200); }
  if (status === 401) return t('#AgentProviderKeyRejected');
  if (status === 402) return detail ? t('#AgentProviderNoFundsDetail', detail) : t('#AgentProviderNoFunds');
  if (status === 429) return t('#AgentProviderRateLimit');
  if (status === 404) return detail ? t('#AgentProviderNotFoundDetail', detail) : t('#AgentProviderNotFound');
  if (status >= 500) return t('#AgentProviderDown');
  return detail ? t('#AgentProviderRejectedDetail', String(status), detail) : t('#AgentProviderRejected', String(status));
}

interface Chunk {
  model?: string;
  choices?: Array<{ delta?: { content?: string | null; reasoning_content?: string | null; tool_calls?: Array<{ index: number; id?: string; function?: { name?: string; arguments?: string } }> }; finish_reason?: string | null }>;
  usage?: { prompt_tokens?: number; completion_tokens?: number } | null;
  error?: { message?: string } | string;
}

export class OpenAICompatibleProvider implements AgentProvider {
  readonly id = 'openai';
  constructor(private readonly options: OpenAICompatibleOptions) {}

  run(request: ProviderRequest): ProviderRun {
    const doFetch = this.options.fetch ?? fetch;
    const body = {
      model: request.model,
      stream: true,
      stream_options: { include_usage: true },
      // Thinking and the answer share the ceiling, so give the run twice the runtime's ask.
      max_tokens: Math.min(request.maxTokens * 2, this.options.maxTokens ?? 32000),
      ...(/deepseek/i.test(this.options.baseURL) || /^deepseek/i.test(request.model) ? { thinking: { type: this.options.thinking ? 'enabled' : 'disabled' } } : {}),
      messages: toChatMessages(request.system, request.messages),
      ...(request.tools.length ? { tools: toChatTools(request.tools), tool_choice: 'auto' } : {}),
    };
    let finalMessage: Message | null = null;
    let failure: unknown = null;
    const events = async function* (this: OpenAICompatibleProvider): AsyncGenerator<StreamEvent> {
      const headers: Record<string, string> = { 'content-type': 'application/json' };
      if (this.options.apiKey) headers.authorization = `Bearer ${this.options.apiKey}`;
      let response: Response;
      try {
        response = await doFetch(`${this.options.baseURL.replace(/\/$/, '')}/chat/completions`, { method: 'POST', headers, body: JSON.stringify(body), signal: request.signal });
      } catch (error) {
        if (request.signal.aborted) throw Object.assign(new Error(t('#AgentStopped')), { name: 'AbortError' });
        throw new ProviderHttpError(0, t('#AgentProviderNoConnection'));
      }
      if (!response.ok) throw new ProviderHttpError(response.status, describeOpenAIError(response.status, await response.text().catch(() => '')));
      if (!response.body) throw new ProviderHttpError(0, t('#AgentProviderNoBody'));
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '', model = request.model, text = '', reasoning = '', textOpen = false, reasoningOpen = false, finish: string | null = null, blockCount = 0;
      const calls = new Map<number, { id: string; name: string; arguments: string; block: number }>();
      const usage: { value: { prompt_tokens?: number; completion_tokens?: number } | null } = { value: null };
      const pending: StreamEvent[] = [];
      const handle = (line: string) => {
        if (!line.startsWith('data:')) return;
        const data = line.slice(5).trim();
        if (!data || data === '[DONE]') return;
        let chunk: Chunk;
        try { chunk = JSON.parse(data) as Chunk; } catch { return; }
        if (chunk.error) throw new ProviderHttpError(400, typeof chunk.error === 'string' ? chunk.error : chunk.error.message ?? t('#AgentProviderError'));
        if (chunk.model) model = chunk.model;
        if (chunk.usage) usage.value = chunk.usage;
        const choice = chunk.choices?.[0];
        if (!choice) return;
        const delta = choice.delta ?? {};
        if (delta.reasoning_content) {
          if (!reasoningOpen) { reasoningOpen = true; pending.push({ type: 'content_block_start', index: blockCount++, content_block: { type: 'thinking', thinking: '', signature: '' } } as StreamEvent); }
          reasoning += delta.reasoning_content;
          pending.push({ type: 'content_block_delta', index: blockCount - 1, delta: { type: 'thinking_delta', thinking: delta.reasoning_content } } as StreamEvent);
        }
        if (delta.content) {
          if (!textOpen) { textOpen = true; pending.push({ type: 'content_block_start', index: blockCount++, content_block: { type: 'text', text: '', citations: null } } as StreamEvent); }
          text += delta.content;
          pending.push({ type: 'content_block_delta', index: blockCount - 1, delta: { type: 'text_delta', text: delta.content } } as StreamEvent);
        }
        for (const call of delta.tool_calls ?? []) {
          let entry = calls.get(call.index);
          if (!entry) {
            entry = { id: call.id || `call_${call.index}_${Date.now().toString(36)}`, name: call.function?.name ?? '', arguments: '', block: blockCount++ };
            calls.set(call.index, entry);
            pending.push({ type: 'content_block_start', index: entry.block, content_block: { type: 'tool_use', id: entry.id, name: entry.name, input: {} } } as StreamEvent);
          } else if (call.function?.name && !entry.name) entry.name = call.function.name;
          if (call.function?.arguments) entry.arguments += call.function.arguments;
        }
        if (choice.finish_reason) finish = choice.finish_reason;
      };
      try {
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';
          for (const line of lines) handle(line.trim());
          while (pending.length) yield pending.shift()!;
        }
        if (buffer.trim()) handle(buffer.trim());
        while (pending.length) yield pending.shift()!;
      } catch (error) {
        failure = error;
        if (request.signal.aborted) throw Object.assign(new Error(t('#AgentStopped')), { name: 'AbortError' });
        throw error;
      }
      const content: Message['content'] = [];
      if (reasoning) content.push({ type: 'thinking', thinking: reasoning, signature: '' } as Message['content'][number]);
      if (text) content.push({ type: 'text', text, citations: null } as Message['content'][number]);
      for (const entry of [...calls.values()].sort((a, b) => a.block - b.block)) {
        let input: unknown = {};
        try { input = entry.arguments ? JSON.parse(entry.arguments) : {}; } catch { input = { _raw: entry.arguments }; }
        content.push({ type: 'tool_use', id: entry.id, name: entry.name, input } as Message['content'][number]);
      }
      const stopReason: Message['stop_reason'] = calls.size ? 'tool_use' : finish === 'length' ? 'max_tokens' : 'end_turn';
      finalMessage = {
        id: 'msg_' + Math.random().toString(36).slice(2, 10), type: 'message', role: 'assistant', model, content, stop_reason: stopReason, stop_sequence: null, stop_details: null,
        usage: { input_tokens: usage.value?.prompt_tokens ?? 0, output_tokens: usage.value?.completion_tokens ?? 0, cache_creation_input_tokens: 0, cache_read_input_tokens: 0, cache_creation: null, server_tool_use: null, service_tier: null, inference_geo: null, iterations: null, speed: null },
      } as unknown as Message;
      yield { type: 'message_delta', delta: { stop_reason: stopReason, stop_sequence: null }, usage: finalMessage.usage } as unknown as StreamEvent;
      yield { type: 'message_stop' } as StreamEvent;
    }.call(this);
    // The runtime iterates the events first and asks for the message after; both share one stream.
    const iterator = events[Symbol.asyncIterator]();
    let drained: Promise<void> | null = null;
    const drain = async () => { for (;;) { const { done } = await iterator.next(); if (done) break; } };
    return {
      events: { [Symbol.asyncIterator]: () => iterator },
      final: async () => {
        drained ??= drain();
        await drained;
        if (failure) throw failure;
        if (!finalMessage) throw new ProviderHttpError(0, t('#AgentProviderClosed'));
        return finalMessage;
      },
    };
  }
}
